/**
 * Notify service for module mwApp.common
 */

/**
 * notifySvc service <br>
 * Show notifications
 *
 * @class notifySvc
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc service
 * @name mwApp/common.service:notifySvc
 */
/* @ngInject */
export default function NotifySvc (storageSvc) {
  /* === private === */

  /**
   * Storage, used for store notifications
   *
   * @private
   * @memberof mwApp/common.service:notifySvc~
   * @type {mwApp/common.service:storageSvc}
   */
  var storage = storageSvc.session // use sessionStorage

  /**
   * @typedef {Object} Notification
   * @property {Number} id  - notification id
   * @property {Date} time  - notification date/time
   * @property {String} msg - notification message
   * @memberof mwApp/common.service:notifySvc
   * @description Describes notification object, it is possible to add other fields in the future
   */

  /**
   * All notifications
   *
   * @private
   * @memberof mwApp/common.service:notifySvc~
   * @type {mwApp/common.service:notifySvc.Notification[]}
   */
  var notifications = load()

  /* === public === */

  this.all = all
  this.clear = clear
  this.empty = empty
  this.get = get
  this.put = put
  this.remove = remove
  this.size = size

  /**
   * Add notification (alias for {@link mwApp/common.service:notifySvc#put})
   *
   * @name mwApp/common.service:notifySvc#add
   * @function
   * @param {String|mwApp/common.service:notifySvc.Notification} msg - object with notification structure, or just message as string
   */
  this.add = put // alias for put

  /**
   * Add notification (alias for {@link mwApp/common.service:notifySvc#put})
   *
   * @name mwApp/common.service:notifySvc#set
   * @function
   * @param {String|mwApp/common.service:notifySvc.Notification} msg - object with notification structure, or just message as string
   */
  this.set = put // alias for put

  /* === implementation === */

  /**
   * Return all notification
   *
   * @memberof mwApp/common.service:notifySvc#
   * @return {mwApp/common.service:notifySvc.Notification[]}
   */
  function all () {
    return notifications
  }

  /**
   * Return count of all notifications
   *
   * @memberof mwApp/common.service:notifySvc#
   * @return {Number}
   */
  function size () {
    return notifications.length
  }

  /**
   * Return true, if there is no notifications, false otherwise
   *
   * @memberof mwApp/common.service:notifySvc#
   */
  function empty () {
    return notifications.length === 0
  }

  /**
   * Return single notification item
   *
   * @memberof mwApp/common.service:notifySvc#
   * @param {Number} id - notification id
   * @return {mwApp/common.service:notifySvc.Notification}
   */
  function get (id) {
    for (var i = notifications.length; i--;) {
      if (id === notifications[i].id) {
        return notifications[i]
      }
    }
  }

  /**
   * Add notification
   *
   * @memberof mwApp/common.service:notifySvc#
   * @param {String|mwApp/common.service:notifySvc.Notification} msg - object with notification structure, or just message as string
   */
  function put (msg) {
    if (typeof msg === 'string') {
      msg = { msg: msg }
    }
    msg.id = notifications.length
    msg.time = new Date()
    msg.remove = remove // remove() method
    notifications.push(msg)
    save()
  }

  /**
   * Remove single notification item
   *
   * @memberof mwApp/common.service:notifySvc#
   * @param {Number} id - notification id
   */
  function remove (id) {
    // add posibility to add remove() as method to notification items themselves
    if (id === undefined && this.id !== undefined) {
      id = this.id
    }

    var index = -1
    for (var i = notifications.length; i--;) {
      if (id === notifications[i].id) {
        index = i
        break
      }
    }
    if (index >= 0) {
      notifications.splice(index, 1)
    }
    save()
  }

  /**
   * Remove all items from notifications
   *
   * @memberof mwApp/common.service:notifySvc#
   */
  function clear () {
    notifications.length = 0
    save()
  }

  /**
   * Load notifications from storage
   *
   * @private
   * @memberof mwApp/common.service:notifySvc~
   * @return {mwApp/common.service:notifySvc.Notification[]} notifications array
   */
  function load () {
    var nArr = storage.get('notifications', [])
    for (var i = nArr.length; i--;) {
      nArr[i].id = i
      nArr[i].time = new Date(nArr[i].time) // create Date objects from timestamp
      nArr[i].remove = remove // remove() method
    }
    return nArr
  }

  /**
   * Save notifications to storage
   *
   * @private
   * @memberof mwApp/common.service:notifySvc~
   */
  function save () {
    var nArr = angular.copy(notifications)
    for (var i = nArr.length; i--;) {
      delete nArr[i].id
      delete nArr[i].remove
      nArr[i].time = +nArr[i].time // convert Date object to timestamp
    }
    storage.set('notifications', nArr)
  }
}
